import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative flex flex-col items-start w-full p-2 gap-0.5 text-xs border overflow-hidden rounded-lg",
  {
    variants: {
      variant: {
        destructive: "alert-destructive bg-destructive/5 text-muted-foreground border-destructive/80",
        warning: "alert-warning bg-warning/5 text-muted-foreground border-warning/80",
        info: "alert-info bg-info/5 text-muted-foreground border-info/80",
        indigo: "alert-indigo bg-indigo-500/5 text-muted-foreground border-indigo-500/80",
        // Paints in `--status-accent` from an ancestor's style (statusAccentStyle).
        accent: "alert-accent bg-(--status-accent)/5 text-muted-foreground border-(--status-accent)/80",
        neutral: "bg-neutral-100/20 text-neutral-800 border-neutral-400 dark:bg-neutral-800/20 dark:text-neutral-200 dark:border-neutral-700",
        success: "alert-success bg-success/5 text-muted-foreground border-success/80",
      },
    },
    defaultVariants: {
      variant: "destructive",
    },
  }
)

const alertBackgroundVariants = cva(
  "absolute inset-0 bg-fixed opacity-30",
  {
    variants: {
      variant: {
        destructive: "bg-[image:repeating-linear-gradient(315deg,_theme(colors.destructive.DEFAULT/0.3)_0,_theme(colors.destructive.DEFAULT/0.5)_1px,_transparent_0,_transparent_50%)] bg-[size:10px_10px]",
        warning: "bg-[image:repeating-linear-gradient(315deg,_theme(colors.warning.DEFAULT/0.3)_0,_theme(colors.warning.DEFAULT/0.5)_1px,_transparent_0,_transparent_50%)] bg-[size:10px_10px]",
        info: "bg-[image:repeating-linear-gradient(315deg,_color-mix(in_oklab,var(--info)_30%,transparent)_0,_color-mix(in_oklab,var(--info)_50%,transparent)_1px,_transparent_0,_transparent_50%)] bg-[size:10px_10px]",
        indigo: "bg-[image:repeating-linear-gradient(315deg,_theme(colors.indigo.500/0.3)_0,_theme(colors.indigo.500/0.5)_1px,_transparent_0,_transparent_50%)] bg-[size:10px_10px]",
        accent: "bg-[image:repeating-linear-gradient(315deg,_color-mix(in_oklab,var(--status-accent)_30%,transparent)_0,_color-mix(in_oklab,var(--status-accent)_50%,transparent)_1px,_transparent_0,_transparent_50%)] bg-[size:10px_10px]",
        neutral: "bg-[image:repeating-linear-gradient(315deg,_theme(colors.neutral.900/0.4)_0,_theme(colors.neutral.100/0.5)_1px,_transparent_0,_transparent_50%)] bg-[size:10px_10px]",
        success: "bg-[image:repeating-linear-gradient(315deg,_theme(colors.success.DEFAULT/0.3)_0,_theme(colors.success.DEFAULT/0.5)_1px,_transparent_0,_transparent_50%)] bg-[size:10px_10px]",
      },
    },
  }
)



function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <div className={alertBackgroundVariants({ variant })} />
      {props.children}
    </div>
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "relative flex items-center gap-2 z-auto text-sm font-medium [&>svg]:size-4 [&>svg]:shrink-0",
        "in-[.alert-destructive]:text-destructive in-[.alert-destructive]:dark:text-destructive",
        "in-[.alert-warning]:text-warning in-[.alert-warning]:dark:text-warning", 
        "in-[.alert-info]:text-info",
        "in-[.alert-indigo]:text-indigo-500 in-[.alert-indigo]:dark:text-indigo-500",
        "in-[.alert-accent]:text-(--status-accent)",
        "in-[.alert-neutral]:text-neutral-800 in-[.alert-neutral]:dark:text-neutral-200",
        "in-[.alert-success]:text-success in-[.alert-success]:dark:text-success",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "relative text-start text-xs z-auto pl-6",
        "in-[.alert-destructive]:text-destructive/90 in-[.alert-destructive]:dark:text-destructive",
        "in-[.alert-warning]:text-warning/90 in-[.alert-warning]:dark:text-warning",
        "in-[.alert-info]:text-blue-700/90 in-[.alert-info]:dark:text-blue-500", 
        "in-[.alert-indigo]:text-indigo-500/90 in-[.alert-indigo]:dark:text-indigo-500",
        "in-[.alert-accent]:text-(--status-accent)/90",
        "in-[.alert-neutral]:text-neutral-800/90 in-[.alert-neutral]:dark:text-neutral-200",
        "in-[.alert-success]:text-success/90 in-[.alert-success]:dark:text-success",
        "text-muted-foreground",
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }